import React from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import {
  FileUpload,
  type FileUploadUploadEvent,
  type FileUploadSelectEvent,
  type ItemTemplateOptions,
  type FileUploadHeaderTemplateOptions,
  type FileUploadHandlerEvent,
} from 'primereact/fileupload';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import logoScreenshot from '../../../../assets/img_logo_scs.png';
import uploadIcon from '../../../../assets/ic_upload_file.png';
import OpenAPI from '../../../../endpoint/Openapi';
import useTenantStore, { selectTenantId } from '../../../../stores/TenantStore';
import { type CustomError } from '../../../../models/Error';

interface IFile {
  name: string;
  objectURL: string;
  size: number;
}

function Logo() {
  const tenantId = useTenantStore(selectTenantId);
  const [totalSize, setTotalSize] = React.useState(0);
  const [selectedFile, setSelectedFile] = React.useState<File>();
  const [pending, setPending] = React.useState(false);
  const fileUploadRef = React.useRef<FileUpload>(null);
  const toast = React.useRef<Toast>(null);

  const handleSave = async () => {
    if (!selectedFile) return;
    setPending(true);

    try {
      const res = await OpenAPI.POST('/api/v2/admin/tenant/asset', {
        params: {
          query: {
            tenantId,
          },
        },
        body: {
          // @ts-expect-error
          File: selectedFile,
          AppAssetsType: 'Logo',
        },
        bodySerializer: (body) => {
          if (!body) return body;

          const formData = new FormData();
          if (body.File) formData.set('file', body.File);
          if (body.AppAssetsType)
            formData.set('appAssetsType', body.AppAssetsType);
          return formData;
        },
      });
      if (res.error) {
        // @ts-expect-error
        const err = res.error;
        toast.current?.show({
          severity: 'error',
          summary: 'Error occurred',
          detail:
            typeof err === 'string'
              ? err
              : err instanceof Error
                ? err.message
                : (err as CustomError)?.message || 'Unknown error',
          life: 3000,
        });
      } else {
        setSelectedFile(undefined);
        toast.current?.show({
          severity: 'info',
          summary: 'Success',
          detail: 'Logo Uploaded',
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error occurred',
        detail:
          typeof err === 'string'
            ? err
            : err instanceof Error
              ? err.message
              : (err as CustomError)?.message || 'Unknown error',
        life: 3000,
      });
    }

    setPending(false);
  };

  const myUploader = (event: FileUploadHandlerEvent) => {
    setSelectedFile(event.files[0]);
    event.options.clear();
  };

  const onTemplateSelect = (e: FileUploadSelectEvent) => {
    setSelectedFile(e.files[0]);
    let _totalSize = totalSize;
    const files = e.files;

    files.forEach((file) => {
      _totalSize += file.size || 0;
    });

    setTotalSize(_totalSize);
  };

  const onTemplateUpload = (e: FileUploadUploadEvent) => {
    let _totalSize = 0;

    e.files.forEach((file) => {
      _totalSize += file.size || 0;
    });

    setTotalSize(_totalSize);
    setSelectedFile(undefined);
    setPending(false);
  };

  const onTemplateRemove = (file: IFile, callback: () => void) => {
    setTotalSize(totalSize - file.size);
    callback();
    setSelectedFile(undefined);
  };

  const onTemplateClear = () => {
    setTotalSize(0);
  };

  const headerTemplate = (options: FileUploadHeaderTemplateOptions) => {
    return (
      <div className="flex flex-row mb-4 align-items-center">
        {options.chooseButton}
      </div>
    );
  };

  const itemTemplate = (file: object, props: ItemTemplateOptions) => {
    return (
      <div className="flex items-center">
        <img
          alt={(file as IFile).name}
          role="presentation"
          src={(file as IFile).objectURL}
          className="w-2/5"
        />
        <div className="flex flex-1 flex-col text-left text-xl ml-3">
          {(file as IFile).name}
          <small>{new Date().toLocaleDateString()}</small>
          <Tag
            value={props.formatSize}
            severity="warning"
            className="px-3 py-2"
          />
        </div>
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-outlined p-button-rounded p-button-danger ml-3"
          onClick={() => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            onTemplateRemove(file as IFile, props.onRemove);
          }}
        />
      </div>
    );
  };

  const emptyTemplate = () => {
    return (
      <div className="flex flex-col items-center px-5 py-3.5 mt-5 text-sm leading-5 text-zinc-500">
        <img loading="lazy" src={uploadIcon} className="aspect-1 w-[60px]" />
        <div className="mt-11 text-base font-bold leading-6">
          drag and drop.
        </div>
        <div className="mt-2">Maximum file size is 100MB.</div>
        <div className="mt-12">File supported: PNG</div>
      </div>
    );
  };

  return (
    <div>
      {pending ? (
        <Skeleton width="100rem" height="100rem"></Skeleton>
      ) : (
        <div className="gap-4">
          <div className="flex flex-row w-full max-w-screen-lg items-start">
            <div className="flex flex-col mr-4 w-full bg-zinc-700 rounded-3xl">
              <img src={logoScreenshot} />
            </div>

            <div className="flex flex-col justify-center mx-auto w-[40%] text-base leading-6 text-black bg-white">
              <div className="flex flex-col px-6 py-8 w-full bg-white rounded-3xl shadow-[0px_4px_12px_rgba(0,0,0,0.1)]">
                <div className="text-3xl font-medium mb-2">Logo</div>

                <FileUpload
                  ref={fileUploadRef}
                  name="logo[]"
                  url="/api/upload"
                  accept="image/png"
                  maxFileSize={100000000}
                  onUpload={onTemplateUpload}
                  onSelect={onTemplateSelect}
                  onError={onTemplateClear}
                  onClear={onTemplateClear}
                  headerTemplate={headerTemplate}
                  itemTemplate={itemTemplate}
                  emptyTemplate={emptyTemplate}
                  chooseOptions={{
                    label: 'Click to upload',
                    icon: ' ',
                    className: '',
                  }}
                  customUpload={true}
                  uploadHandler={myUploader}
                  contentClassName="bg-white rounded-2xl border border-dashed border-neutral-400 px-0 py-0"
                />

                <div className="mt-5 leading-7 text-zinc-500">
                  If a logo is not provided, the Driverly logo will be used.
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-row w-full px-6 py-6 justify-end">
            <Button
              label="Save"
              className={`text-2xl px-14 py-3 bg-[#F1B51C] hover:bg-bluegray-400 text-black border-none`}
              onClick={handleSave}
            />
          </div>
        </div>
      )}

      <Toast ref={toast} className="mt-header-height" />
    </div>
  );
}

export default Logo;
